import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Gauge, CheckCircle2, Clock, AlertTriangle, XCircle, Thermometer, TrendingUp, ChevronRight, ClipboardCheck, RotateCcw } from "lucide-react";
import { PageHeader, EmptyState } from "@/components/common";
import { StatusPill } from "@/components/StatusPill";
import { LocationSelect } from "@/components/LocationSelect";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { fmtTemp, fmtDate, elapsed } from "@/lib/format";

const ATTENTION_LABEL = {
  OPEN_INCIDENT: "Incidencia abierta", SECOND_MEASUREMENT: "Segunda medición pendiente",
  MISSED: "Control omitido", LATE: "Control atrasado",
};

export default function Dashboard() {
  const { activeLocation } = useAuth();
  const [data, setData] = useState(null);
  const [pl, setPl] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    const q = activeLocation ? `?location_id=${activeLocation}` : "";
    api.get(`/dashboard${q}`).then(({ data }) => setData(data)).catch(() => {});
    api.get(`/planillas/dashboard${q}`).then(({ data }) => setPl(data)).catch(() => {});
  }, [activeLocation]);

  const k = data?.kpis;
  return (
    <div>
      <PageHeader title="Temperaturas de hoy"
        subtitle={fmtDate(new Date())}
        right={<LocationSelect allOption />} />

      {!data ? (
        <div className="py-10 text-center text-slate-400">Cargando…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Kpi icon={Gauge} label="Cumplimiento hoy" value={`${k.compliance}%`} accent testid="kpi-compliance" />
            <Kpi icon={CheckCircle2} label="Realizados" value={k.completed} sub={`de ${k.expected} esperados`} testid="kpi-completed" />
            <Kpi icon={Clock} label="Pendientes" value={k.pending} testid="kpi-pending" />
            <Kpi icon={AlertTriangle} label="Atrasados" value={k.late} testid="kpi-late" />
            <Kpi icon={XCircle} label="Omitidos" value={k.missed} testid="kpi-missed" />
            <Kpi icon={Thermometer} label="Fuera de rango" value={k.out_of_range} testid="kpi-outofrange" />
            <Kpi icon={AlertTriangle} label="Incidencias abiertas" value={k.incidents_open} testid="kpi-incidents" />
            <Kpi icon={RotateCcw} label="Recontroles pend." value={k.recheck_pending ?? 0} testid="kpi-recheck" />
            <Kpi icon={TrendingUp} label="Esperados" value={k.expected} testid="kpi-expected" />
          </div>

          {data.by_shift?.length > 0 && (
            <div className="mt-6">
              <h2 className="mb-3 font-display text-lg font-semibold">Temperaturas por turno</h2>
              <div className="grid gap-3 sm:grid-cols-2">
                {data.by_shift.map((s) => (
                  <div key={s.shift_id} data-testid={`temp-shift-${s.shift_id}`} className="rounded-2xl border border-[#E2E8F0] bg-white p-4">
                    <div className="flex items-center justify-between"><p className="font-semibold text-[#153D35]">{s.shift_name}</p><span className="tnum text-lg font-semibold text-[#2563EB]">{s.compliance}%</span></div>
                    <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-100"><div className="h-full bg-[#2563EB]" style={{ width: `${s.compliance}%` }} /></div>
                    <p className="mt-2 text-xs text-[#68756F]">Realizados: {s.completed} · Pendientes: {s.pending} · Atrasados: {s.late} · Fuera de rango: {s.incident}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-8 grid gap-6 lg:grid-cols-3">
            <section className="lg:col-span-2">
              <h2 className="mb-3 font-display text-lg font-semibold">Requiere atención</h2>
              {data.requires_attention.length === 0 ? (
                <EmptyState icon={CheckCircle2} title="Nada urgente" subtitle="No hay elementos que requieran atención ahora." />
              ) : (
                <div className="space-y-3">
                  {data.requires_attention.map((a, i) => (
                    <button key={i} data-testid={`attention-item-${i}`}
                      onClick={() => a.incident_id && nav(`/admin/incidencias/${a.incident_id}`)}
                      className="flex w-full items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-left transition-shadow hover:shadow-sm">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <StatusPill status={a.kind === "SECOND_MEASUREMENT" ? "WAITING_RECHECK" : a.status} label={ATTENTION_LABEL[a.kind]} />
                          {a.severity && <StatusPill status={a.severity} />}
                        </div>
                        <p className="mt-1.5 font-medium text-slate-900">{a.equipment_name}</p>
                        <p className="text-xs text-slate-500">{a.location_name} · {elapsed(a.elapsed_since)}</p>
                      </div>
                      {a.value != null && <span className="tnum font-display text-lg font-semibold text-red-600">{fmtTemp(a.value, a.unit)}</span>}
                      <ChevronRight className="h-5 w-5 text-slate-300" />
                    </button>
                  ))}
                </div>
              )}
            </section>

            <section>
              <h2 className="mb-3 font-display text-lg font-semibold">Estado por local</h2>
              <div className="space-y-3">
                {data.per_location.map((l) => {
                  const tone = l.compliance >= 90 ? "emerald" : l.compliance >= 70 ? "amber" : "red";
                  const toneCls = { emerald: "bg-emerald-500", amber: "bg-amber-500", red: "bg-red-500" }[tone];
                  return (
                    <div key={l.location_id} data-testid={`loc-status-${l.location_id}`} className="rounded-2xl border border-slate-200 bg-white p-4">
                      <div className="flex items-center justify-between">
                        <p className="font-medium">{l.location_name}</p>
                        <span className="tnum font-display text-lg font-semibold">{l.compliance}%</span>
                      </div>
                      <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-100">
                        <div className={`h-full ${toneCls}`} style={{ width: `${l.compliance}%` }} />
                      </div>
                      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                        <span>Pendientes: <b className="text-slate-700">{l.pending}</b></span>
                        <span>Alertas: <b className="text-slate-700">{l.alerts_new}</b></span>
                        <span>Incidencias: <b className="text-slate-700">{l.incidents_open}</b></span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          </div>

          {pl && (
            <section className="mt-8">
              <h2 className="mb-3 font-display text-lg font-semibold">Planillas de hoy</h2>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Kpi icon={ClipboardCheck} label="Controles de hoy" value={`${pl.kpis.done} / ${pl.kpis.total}`} testid="pl-kpi-today" />
                <Kpi icon={Gauge} label="Cumplimiento" value={`${pl.kpis.compliance}%`} accent testid="pl-kpi-compliance" />
                <Kpi icon={Clock} label="Pendientes" value={pl.kpis.pending} testid="pl-kpi-pending" />
                <Kpi icon={AlertTriangle} label="Incidencias" value={pl.kpis.incidents} testid="pl-kpi-incidents" />
              </div>
              {pl.per_location.length > 1 && (
                <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {pl.per_location.map((l) => (
                    <div key={l.location_id} className="flex items-center justify-between rounded-xl border border-[#E2E8F0] bg-white px-4 py-3 text-sm">
                      <span className="font-medium text-[#153D35]">{l.location_name}</span>
                      <span className="tnum font-semibold">{l.done}/{l.total}</span>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}
        </>
      )}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub, accent, testid }) {
  return (
    <div data-testid={testid} className={`rounded-2xl border bg-white p-4 ${accent ? "border-[#2563EB]/30 ring-1 ring-[#2563EB]/10" : "border-[#E2E8F0]"}`}>
      <div className="grid h-9 w-9 place-items-center rounded-xl bg-[#EFF4FE]">
        <Icon className="h-[18px] w-[18px] text-[#2563EB]" />
      </div>
      <p className="tnum mt-3 font-display text-3xl font-semibold text-[#153D35]">{value}</p>
      <p className="text-xs text-[#68756F]">{label}</p>
      {sub && <p className="text-[11px] text-slate-400">{sub}</p>}
    </div>
  );
}
